import type { Video } from "@brookbush/types";

import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

interface VideoTranscriptProps {
	video: Video;
	isMobile?: boolean;
}

const VideoTranscript = ({ isMobile, video }: VideoTranscriptProps) => {
	const [currentTranscript, setCurrentTranscript] = useState(0);
	const currentTranscriptRef = useRef(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const timeMap = useMemo(() => {
		if (!video.captions) return new Map();

		const map = new Map();
		video.captions.forEach((caption) => {
			for (
				let t = Math.floor(caption.startTime);
				t <= Math.floor(caption.endTime);
				t++
			) {
				map.set(t, caption);
			}
		});
		return map;
	}, [video.captions]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we need to use isMobile here to re-run the effect on mobile changes
	useEffect(() => {
		if (!video.captions) return;

		const muxPlayer = document.querySelector("mux-player");
		if (!muxPlayer) return;

		const handleTimeUpdate = (event: Event) => {
			const currentTime = (event.target as HTMLVideoElement).currentTime;
			const currentSecond = Math.floor(currentTime);
			const caption = timeMap.get(currentSecond);

			if (!caption || currentTranscriptRef.current === caption.startTime) {
				return;
			}

			currentTranscriptRef.current = caption.startTime;
			setCurrentTranscript(caption.startTime);

			const element = document.getElementById(caption.id);
			const container = containerRef.current;

			if (element && container) {
				const elementTop = element.offsetTop;
				const elementHeight = element.offsetHeight;
				const containerHeight = container.clientHeight;

				const scrollToPosition =
					elementTop - containerHeight / 2 + elementHeight / 2;

				container.scrollTo({
					top: scrollToPosition,
					behavior: "smooth",
				});
			}
		};

		muxPlayer.addEventListener("timeupdate", handleTimeUpdate);

		return () => {
			muxPlayer.removeEventListener("timeupdate", handleTimeUpdate);
		};
	}, [video.captions, timeMap, isMobile]);

	const getTimestamp = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = Math.floor(seconds % 60);
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	if (!video.captions?.length) return null;

	return (
		<div className="flex h-full flex-col phablet:px-4 px-3">
			<div
				ref={containerRef}
				className="no-scrollbar flex-1 phablet:overflow-y-auto scroll-smooth phablet:py-2.5 py-2"
			>
				<div className="space-y-4">
					{video.captions.map((caption) => (
						<button
							onClick={() => {
								(
									document.querySelector("mux-player")! as HTMLVideoElement
								).currentTime = caption.startTime;
							}}
							id={caption.id}
							key={caption.id}
							className={clsx(
								"group flex w-full items-start gap-3 phablet:gap-4 text-left transition-colors hover:underline",
								{
									"font-semibold": currentTranscript === caption.startTime,
								},
							)}
						>
							<span className="mt-1 phablet:text-sm text-slate-500 text-xs dark:text-slate-300">
								{getTimestamp(caption.startTime)}
							</span>

							<span className="phablet:text-base text-sm">
								{caption.caption}
							</span>
						</button>
					))}
				</div>
			</div>
		</div>
	);
};

export default VideoTranscript;
